import Gooey, {
    Component,
    calc,
    Dyn,
    dynGet,
    dynSet,
    dynSubscribe,
    field,
    model,
    ref,
    collection,
} from '@srhazi/gooey';
import './auto-sized-text.css';

const distance = (a: number, b: number) => Math.abs(a - b);

const THRESHOLD = 1;
const MAX_ITER = Infinity;

export const AutoSizedText: Component<{
    label: Dyn<string>;
    width: Dyn<number>;
    debug?: Dyn<boolean>;
    fontWidth: Dyn<number>;
    fontWidthMax?: Dyn<number>;
    fontWidthMin?: Dyn<number>;
    letterSpacing: Dyn<number>;
    letterSpacingMax?: Dyn<number>;
    letterSpacingMin?: Dyn<number>;
}> = (
    {
        label,
        width,
        debug = false,
        fontWidth,
        fontWidthMax = fontWidth,
        fontWidthMin = fontWidth,
        letterSpacing,
        letterSpacingMin = letterSpacing,
        letterSpacingMax = letterSpacing,
    },
    { onMount }
) => {
    const span = document.createElement('span');
    span.className = 'auto-sized-text__label';
    const lastLetterSpan = document.createElement('span');
    lastLetterSpan.className = 'auto-sized-text__label__last-letter';
    const measure = (property: string, tryValue: number) => {
        span.style.setProperty(property, tryValue.toString());
        const width = span.getBoundingClientRect().width;
        return width;
    };

    const binarySearch = (
        targetWidth: number,
        property: string,
        minValue: number,
        maxValue: number
    ) => {
        let minMeasured = measure(property, minValue);
        if (targetWidth < minMeasured) {
            return false;
        }
        let maxMeasured = measure(property, maxValue);
        if (targetWidth > maxMeasured) {
            return false;
        }
        let nextMeasured = maxMeasured;
        let count = 0;
        while (distance(nextMeasured, targetWidth) > THRESHOLD) {
            const pct =
                (targetWidth - minMeasured) / (maxMeasured - minMeasured);
            const nextWidth = minValue + (maxValue - minValue) * pct;
            nextMeasured = measure(property, nextWidth);
            if (nextMeasured < targetWidth) {
                minValue = nextWidth;
            } else {
                maxValue = nextWidth;
            }
            count++;
            if (count > MAX_ITER) {
                return false;
            }
        }
        return true;
    };

    const reset = () => {
        span.style.setProperty(
            '--auto-sized-text-width',
            dynGet(fontWidth).toString()
        );
        span.style.setProperty(
            '--auto-sized-letter-spacing',
            dynGet(letterSpacing).toString()
        );
    };

    const remeasure = () => {
        reset();
        const currentWidth = span.getBoundingClientRect().width;
        const targetWidth = dynGet(width);
        if (distance(currentWidth, targetWidth) <= THRESHOLD) {
            return;
        }
        const result = binarySearch(
            targetWidth,
            '--auto-sized-text-width',
            dynGet(fontWidthMin),
            dynGet(fontWidthMax)
        );
        if (!result) {
            binarySearch(
                targetWidth,
                '--auto-sized-letter-spacing',
                dynGet(letterSpacingMin),
                dynGet(letterSpacingMax)
            );
        }
    };

    const setLabel = (value: string) => {
        const labelStr = value.trim();
        const prefix = labelStr.slice(0, -1);
        const suffix = labelStr[labelStr.length - 1];
        lastLetterSpan.textContent = suffix;
        span.replaceChildren(prefix, lastLetterSpan);
    };

    onMount(() => {
        setLabel(dynGet(label));
        let isMounted = true;
        if (document.fonts.status === 'loading') {
            document.fonts.ready.then(() => {
                if (isMounted) {
                    remeasure();
                }
            });
        } else {
            remeasure();
        }
        const ancestry = [];
        let x: Node | null = span;
        while (x) {
            ancestry.push(x);
            x = x.parentNode;
        }
        const unsubscriptions = [
            width,
            fontWidthMax,
            fontWidthMin,
            letterSpacingMin,
            letterSpacingMax,
        ].map((val) => dynSubscribe(val, remeasure));
        unsubscriptions.push(
            dynSubscribe(label, (val) => {
                setLabel(val);
                remeasure();
            })
        );
        return () => {
            isMounted = false;
            unsubscriptions.forEach((unsubscribe) => unsubscribe());
        };
    });
    return <span class="auto-sized-text">{span}</span>;
};
